'use client';
import Button from '../Button/Button';
import cx from 'classnames';
import ReactModal from 'react-modal';
import { isBrowser } from '../../util/ssr';

import PlaceBetIllustration from './assets/redirect-illustration.svg';
import styles from './RedirectModal.module.scss';

export type RedirectModalProps = {
  /**
   * Controls if the modal should be shown or not.
   */
  isOpen: boolean;
  /**
   * Controls which modal version is shown
   */
  isMobile: boolean;
  /**
   * URL to redirect the user to, eg. Sportsbook betslip
   *
   * For desktop: triggered by green 'Go to betslip' button
   *
   * For mobile: triggered by blue 'Open Sportsbook app' button
   */
  redirectUrl: string;
  /**
   * URL where mobile user can download app (applied only when `isMobile` is set to `true`)
   *
   * Triggered by green 'Download sportsbook app' button
   */
  appStoreUrl?: string;
  /**
   * Function that will be run when user interacts with primary CTA
   *
   * For desktop: triggered by click on green 'Go to betslip' button
   *
   * For mobile: triggered by tap on green 'Download Sportsbook app' button
   *
   * Example use-case: user interaction analytics
   */
  onClickPrimaryCta?: () => void;
  /**
   * Optional onClick event for secondary CTA
   *
   * For mobile: triggered by tap on blue 'Open Sportsbook app' button
   *
   * Example use-case: user interaction analytics
   */
  onClickSecondaryCta?: () => void;
  /**
   * Function that will be run when the modal is requested to be closed, prior to actually closing.
   */
  onRequestClose?: () => void;
  /**
   * The app element allows you to specify the portion of your app that should be hidden (via aria-hidden) to prevent assistive technologies such as screenreaders from reading content outside of the content of your modal.
   *
   * Defaults to `'#__next'`
   */
  appElement?: string;
};

const RedirectModal: React.FC<RedirectModalProps> = ({
  isOpen,
  redirectUrl,
  isMobile,
  onClickPrimaryCta,
  onRequestClose,
  appElement = '#__next',
  onClickSecondaryCta,
  appStoreUrl,
}: RedirectModalProps) => {
  const modalClass = cx(
    styles.modal,
    styles.defaultDimensions,
    isMobile ? styles.mobileMod : styles.mod
  );

  if (process.env.NODE_ENV === 'test') {
    // required by unit tests in the library and the consuming apps
    ReactModal.setAppElement('div');
  } else if (isBrowser()) {
    ReactModal.setAppElement(appElement);
  }

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName={styles.overlay}
      className={modalClass}
      aria-labelledby='dialog-title'
      aria-describedby='dialog-desc'
    >
      <div className={styles.content} data-test-id='redirect-modal'>
        <div className={styles.illustration}>
          <PlaceBetIllustration style={{ width: 215, height: 137 }} />
        </div>
        <div className={styles.titleText} id='dialog-title'>
          <span>Ready to place a bet?</span>
        </div>

        <p className={styles.subText} id='dialog-desc'>
          {isMobile
            ? "To start placing bets, you'll need to download the app."
            : "To place a bet, you'll need to log in."}
        </p>

        <div className={styles.buttonsContainer}>
          <a
            href={isMobile ? appStoreUrl : redirectUrl}
            target={isMobile ? '_blank' : undefined}
            rel={isMobile ? 'noopener noreferrer' : undefined}
          >
            <Button
              className={styles.button}
              buttonColor='Green'
              testId='primary-cta'
              buttonText={
                isMobile ? 'Download Sportsbook app' : 'Go to betslip'
              }
              onClick={onClickPrimaryCta}
            />
          </a>

          {isMobile && (
            <a href={redirectUrl}>
              <Button
                className={styles.button}
                testId='secondary-cta'
                buttonText='Open Sportsbook app'
                onClick={onClickSecondaryCta}
              />
            </a>
          )}

          <Button
            buttonStyle='Naked'
            className={styles.button}
            onClick={onRequestClose}
            buttonText='Never mind'
            testId='close-button'
          />
        </div>
      </div>
    </ReactModal>
  );
};

export default RedirectModal;
